import _ from 'lodash';
import { couponDiscountTypeMap } from './constant';

/**
 * Pure function: Get discount and discountType information for a specific plan
 * @param {string} planCode - Plan code to get discount info for
 * @param {Object} discountWithAmountMap - Discount with amount map object (should contain discountPlans property)
 * @returns {Object|null} Discount information object or null if not found
 * @returns {number} returns.discountAmount - Discount amount for the plan
 * @returns {number} returns.discountType - Discount type (1: percentage, 2: fixed amount, 5: threshold)
 * @returns {string} returns.expireDate - Coupon expire date
 * @returns {number} returns.discount - Original discount value
 * @returns {number} returns.couponPeriod - Coupon period
 * @returns {number} returns.couponDefType - Coupon definition type
 */
export const getPlanDiscountInfo = (planCode, discountWithAmountMap = {}) => {
  if (!planCode) {
    return null;
  }

  const discountPlans = discountWithAmountMap?.discountPlans || {};
  const planDiscount = discountPlans[planCode];

  if (!planDiscount) {
    return null;
  }

  // Return essential discount information
  return {
    discountAmount: planDiscount.discountAmount || 0,
    discountType: planDiscount.discountType,
    expireDate: planDiscount.expireDate || '',
    discount: planDiscount.discount,
    couponPeriod: planDiscount.couponPeriod,
    couponDefType: planDiscount.couponDefType,
    // Include all other properties for flexibility
    ...planDiscount,
  };
};

/**
 * Pure function: Get discount amount for a specific plan (shorthand method)
 * @param {string} planCode - Plan code to get discount amount for
 * @param {Object} discountWithAmountMap - Discount with amount map object (should contain discountPlans property)
 * @returns {number} Discount amount, 0 if not found
 */
export const getPlanDiscountAmount = (planCode, discountWithAmountMap = {}) => {
  const discountInfo = getPlanDiscountInfo(planCode, discountWithAmountMap);
  return discountInfo?.discountAmount || 0;
};

/**
 * Pure function: Get discount type for a specific plan (shorthand method)
 * @param {string} planCode - Plan code to get discount type for
 * @param {Object} discountWithAmountMap - Discount with amount map object (should contain discountPlans property)
 * @returns {number|undefined} Discount type, undefined if not found
 */
export const getPlanDiscountType = (planCode, discountWithAmountMap = {}) => {
  const discountInfo = getPlanDiscountInfo(planCode, discountWithAmountMap);
  return discountInfo?.discountType;
};

/**
 * Pure function: Check if all discountTypes in discountPlans are percentage
 * @param {Object} discountWithAmountMap - Discount with amount map object (should contain discountPlans property)
 * @returns {boolean} True if all discountTypes are percentage, false otherwise. Returns true if no discountPlans.
 */
export const isPercentageDiscountForCoupon = (discountWithAmountMap = {}) => {
  const discountPlans = discountWithAmountMap?.discountPlans || {};

  return _.chain(discountPlans)
    .values()
    .every((plan) => isPercentageDiscount(plan.discountType))
    .value();
};

/**
 * Pure function: Check if discountType is percentage
 * @param {number} discountType - Discount type value
 * @returns {boolean} True if discountType is percentage, false otherwise
 */
export const isPercentageDiscount = (discountType) => {
  return discountType === couponDiscountTypeMap.percentage;
};

/**
 * Pure function: Check if discountType is fixed amount
 * @param {number} discountType - Discount type value
 * @returns {boolean} True if discountType is fixed amount, false otherwise
 */
export const isFixedAmountDiscount = (discountType) => {
  return discountType === couponDiscountTypeMap.fixedAmount;
};

/**
 * Pure function: Check if discountType is threshold amount
 * @param {number} discountType - Discount type value
 * @returns {boolean} True if discountType is threshold amount, false otherwise
 */
export const isThresholdAmountDiscount = (discountType) => {
  return discountType === couponDiscountTypeMap.thresholdAmount;
};

/**
 * Pure function: Get list of discountPlans from discountWithAmountMap
 * @param {Object} discountWithAmountMap - Discount with amount map object (should contain discountPlans property)
 * @returns {Array} Array of discount plan objects, empty array if no discountPlans
 */
export const getDiscountPlansList = (discountWithAmountMap = {}) => {
  const discountPlans = discountWithAmountMap?.discountPlans || {};
  return Object.values(discountPlans);
};

export const couponHelper = {
  getPlanDiscountInfo,
  getPlanDiscountAmount,
  getPlanDiscountType,
  isPercentageDiscountForCoupon,
  isPercentageDiscount,
  isFixedAmountDiscount,
  isThresholdAmountDiscount,
  getDiscountPlansList,
};
